import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AccountService } from '../../shell/account.service';
import { SessionService } from '../../shell/session.service';
import { Pagination } from '../components/pagination/pagination.component';
import {
  AI_PROCESSING,
  AI_PROCESSING_FAILED,
  CALL_ENDING_TYPE,
  CALL_LOG_EVENT_DIRECTION,
  CALL_LOG_EVENT_TYPE,
  CallLogDateTimeRange,
  CallLogEvent,
  CallLogFilters,
  CallLogFiltersDTO,
  CallLogResponse,
  CallStatus,
  DEFAULT_CALL_HISTORY_PAGE_SIZE,
  UICallLogEvent,
} from '../models/call-history.model';

@Injectable()
export class CallHistoryService {
  private currentSesion: any;
  private headers = new HttpHeaders({
    Authorization: 'Bearer ' + this.account.current.token,
    'Voverc-Jwt-Auth': this.account.current.jwtToken,
  });

  private phoneUtils: PhoneNumberUtil;

  constructor(
    private http: HttpClient,
    private session: SessionService,
    private account: AccountService,
    private sanitizer: DomSanitizer
  ) {
    this.phoneUtils = PhoneNumberUtil.getInstance();
    this.currentSesion = this.session.current;
  }

  public getCallLog(
    filters: CallLogFilters,
    pagination: Pagination,
    accountId: string,
    sortString?: string
  ): Observable<CallLogResponse> {
    const params = {
      sort: sortString ? sortString : 'timestamp,desc',
      size: (pagination.pageSize || DEFAULT_CALL_HISTORY_PAGE_SIZE).toString(),
      page: pagination.page.toString(),
    };
    const payload = {
      ...this.mapFiltersToDTO(accountId, filters),
    };

    return this.http.post<CallLogResponse>(`${environment.API_URL}/api/v1/telephony-events`, payload, {
      params,
      headers: this.headers,
    });
  }

  public getAccountId(): Observable<string> {
    return this.http
      .get(`${environment.API_URL}/api/v1/pbxes/${this.currentSesion.pbxId}`, { headers: this.headers })
      .pipe(map((res: any) => res.kazoo_id));
  }

  public parseCallLogEvent(call: CallLogEvent): UICallLogEvent {
    const answered = this.isAnswered(call);

    // have to use to toLowerCase to avoid not finding proper CRM by name in case of different
    // naming(spotted that with intercom).
    return {
      id: call.id,
      type: call.type,
      number: this.getDidLabel(call),
      contactCrm: call.contactCrm ?? '',
      contactCrmUrl: call.contactCrmUrl ?? '',
      contactCrmIconLink: call.crmLogo,
      from: this.numberParser(call.originatorName),
      to: this.numberParser(call.destinationName),
      fromFull: this.getFullCallee(call, 'originator'),
      toFull: this.getFullCallee(call, 'destination'),
      answerer: this.getAnswerer(call),
      answererFull: this.getFullCallee(call, 'answerer'),
      direction: call.direction,
      duration: answered ? call.duration : this.getDurationFromEntry(call),
      labels: [...call.labels],
      ruleNames: [...call.ruleNames],
      timestamp: call.timestamp,
      recorded: call.recorded,
      recordingUrl: call.recordingUrl || null,
      answered,
      accountId: call.accountId,
      ringTime: call.ringTime,
      maskCallerNumber: call.maskCallerNumber,
      callLinks: {
        from: this.getPhoneLink(call.originatorNumber),
        to: this.getPhoneLink(call.destinationNumber),
        answerer: call.answererNumber ? this.getPhoneLink(call.answererNumber) : '',
      },
      ai: {
        sentimentScore: call.sentiment_score,
        transcription: call.transcription,
        summary: call.summary,
        isProcessing: call.transcription === AI_PROCESSING, // way to mark call being analyzed
        processingFailed: call.transcription === AI_PROCESSING_FAILED, // mark call that failed processing
        hasData: this.hasAiData(call),
      },
      webhooks: call.webhooks || [],
      voicemail: call.voicemail,
      voicemailUrl: call.voicemailUrl,
      sms: call.sms ?? [],
    };
  }

  private mapFiltersToDTO(accountId: string, filters?: CallLogFilters): CallLogFiltersDTO {
    const DTO: CallLogFiltersDTO = {
      account_id: accountId,
      date_time_ranges: filters.date_time_ranges.map((f: CallLogDateTimeRange) => {
        const times = {
          from_time: f.time ? this.convertTime(f.time.from) : f.from_time,
          to_time: f.time ? this.convertTime(f.time.to) : f.to_time,
        };
        const sameRange = times.from_time === '00:00:00' && times.to_time === '00:00:00';

        return sameRange
          ? {
              from: f.time ? `${moment(f.from).format()}[${filters.timezone}]` : f.from,
              to: f.time ? `${moment(f.to).format()}[${filters.timezone}]` : f.to,
            }
          : {
              from: f.time ? `${moment(f.from).format()}[${filters.timezone}]` : f.from,
              to: f.time ? `${moment(f.to).format()}[${filters.timezone}]` : f.to,
              from_time: times.from_time,
              to_time: times.to_time,
            };
      }),
      date_time: filters?.date_time
        ? {
            from: `${moment(filters?.date_time?.from).format()}[${filters.timezone}]`,
            to: `${moment(filters?.date_time?.to).format()}[${filters.timezone}]`,
          }
        : null,
      did_number: filters?.did_number?.number,
      direction: filters?.direction,
      duration: filters?.duration
        ? {
            from: this.parseTimeRange(filters?.duration.from, true),
            to: this.parseTimeRange(filters?.duration.to, true),
          }
        : null,
      ring_time: filters?.ring_time
        ? {
            from: this.parseTimeRange(filters?.ring_time?.from),
            to: this.parseTimeRange(filters?.ring_time?.to),
          }
        : null,
      is_answered: filters.status ? filters?.status === CallStatus.answered : null,
      originator_name: filters?.originator_name || null,
      originator_number: filters?.originator_number || null,
      destination_name: filters?.destination_name || null,
      destination_number: filters?.destination_number || null,
      answerer_name: filters?.answerer_name || null,
      answerer_number: filters?.answerer_number || null,
      event_types: filters?.event_types ? [filters.event_types] : null,
      sentiment_score: filters?.sentiment_score
        ? {
            from: filters?.sentiment_score?.from,
            to: filters?.sentiment_score?.to,
          }
        : null,
      voicemail: filters?.voicemail,
      recorded: filters?.recorded,
      with_sms: filters?.with_sms,
      is_ai_agent: filters?.is_ai_agent,
      user_id: filters?.user?.aggregate_id || null,
    };
    this.cleanObjectFromNull(DTO);

    return DTO;
  }

  private convertTime(date: Date | string): string {
    return moment(date).format('HH:mm:ss');
  }

  private parseTimeRange(tr: string, minutes = false): number {
    if (!tr) return null;
    const number = Number(tr);
    if (isNaN(number)) return null;

    return minutes ? number * 60 : number;
  }

  private cleanObjectFromNull(obj: object): void {
    if (obj === null) return;
    Object.keys(obj).forEach((k: string) => {
      const item = obj[k];
      if (typeof item === 'object' && !Array.isArray(item)) {
        this.cleanObjectFromNull(item);
        if (!item || (item && Object.keys(item).length === 0)) {
          delete obj[k];
        }
      } else {
        if (item === null || item === undefined) {
          delete obj[k];
        }
      }
    });
  }

  private isAnswered(e: CallLogEvent): boolean {
    if (e.duration !== 0) {
      return true;
    }
    switch (e.lastEvent) {
      case CALL_LOG_EVENT_TYPE.CALL_TERMINATED:
        return true;
      case CALL_LOG_EVENT_TYPE.CALL_REJECTED:
      case CALL_LOG_EVENT_TYPE.CALL_LOST_LOSE_RACE:
      case CALL_LOG_EVENT_TYPE.CALL_LOST_NO_ANSWER:
      case CALL_LOG_EVENT_TYPE.CALL_LOST_USER_BUSY:
      case CALL_LOG_EVENT_TYPE.CALL_LOST_BUSY_LINES:
        return false;
    }
  }

  private getDidLabelFromNumber(number: string): string {
    const dids = this.currentSesion.dids;
    const match = dids.find((d) => d.number.includes(number));

    return match ? match.label : this.numberParser(number);
  }

  private getDidLabel(entry: CallLogEvent): string {
    if (entry.direction === CALL_LOG_EVENT_DIRECTION.INBOUND) {
      return this.getDidLabelFromNumber(entry.destinationNumber);
    } else if (entry.direction === CALL_LOG_EVENT_DIRECTION.OUTBOUND) {
      return this.getDidLabelFromNumber(entry.originatorNumber);
    } else {
      return null;
    }
  }

  private numberParser(num: string): string {
    // this is looking like extension
    if (this.stripSpaces(num).length <= 3) {
      return num;
    }
    const replacedZeros = num.replace(/^00/, '+');
    const isMasked = num.includes('*');
    try {
      if (num) {
        const num = this.phoneUtils.parse('+' + replacedZeros);
        if (this.phoneUtils.isValidNumberForRegion(num)) {
          const formatted = this.phoneUtils.format(num, PhoneNumberFormat.NATIONAL);

          return isMasked ? `${formatted}***` : formatted;
        }

        if (this.phoneUtils.isValidNumber(num)) {
          const formatted = this.phoneUtils.format(num, PhoneNumberFormat.INTERNATIONAL);

          return isMasked ? `${formatted}***` : formatted;
        } else {
          const withPlus =
            String(replacedZeros).charAt(0) === '+'
              ? this.phoneUtils.parseAndKeepRawInput(replacedZeros)
              : this.phoneUtils.parseAndKeepRawInput('+' + replacedZeros);

          if (this.phoneUtils.isValidNumber(withPlus)) {
            const formatted = this.phoneUtils.format(withPlus, PhoneNumberFormat.INTERNATIONAL);

            return isMasked ? `${formatted}***` : formatted;
          }
        }
        const formatted = this.phoneUtils.format(num, PhoneNumberFormat.INTERNATIONAL);

        return isMasked ? `${formatted}***` : formatted;
      }
    } catch (e) {
      console.info(`numberParser: Failed to parse number ${num}`, e);

      return replacedZeros;
    }
  }

  private stripSpaces(n: string): string {
    return n.replace(/\s+/g, '');
  }

  private getFullCallee(call: CallLogEvent, direction: 'originator' | 'destination' | 'answerer'): string {
    const name = call[`${direction}Name`] ? this.numberParser(call[`${direction}Name`]) : '';
    const num = call[`${direction}Number`] ? this.numberParser(call[`${direction}Number`]) : '';

    return this.stripSpaces(name) === this.stripSpaces(num) ||
      call[`${direction}Name`].includes(call[`${direction}Number`])
      ? null
      : `${name} [${num}]`;
  }

  private getAnswerer(entry: CallLogEvent): string {
    return this.callingPartyFactory(entry.answererName ?? '', entry.answererNumber ?? '') ?? '';
  }

  private callingPartyFactory(partyName: string, partyNumber: string) {
    /**
     * partyName name is a string like:
     *      *   "verso *name* da *number*",;
     *      *   "*first_name* *last_name*;
     *
     * so if last item is not a number, we use the whole string, otherwise
     * we use only last item (the number)
     */
    let lastItemInPartyName;
    try {
      lastItemInPartyName = partyName.split(' ').splice(-1)[0];
    } catch (e) {
      console.info(`getAnswerer: Failed to extract party name from: ${partyName}`, e);

      return null;
    }

    if (this.isNumber(lastItemInPartyName)) return this.numberParser(lastItemInPartyName);
    else {
      // If it's an extension, it's added to the entry;
      return partyNumber?.length <= 3 ? partyName + ' ' + partyNumber : partyName;
    }
  }

  private isNumber(value: number | Array<any> | string): boolean {
    if (!value || value instanceof Array) return false;
    const numberRegex = /^(\+|-)?\d+$/;

    return typeof value === 'number' || numberRegex.test(value);
  }

  private getPhoneLink(n: string): SafeResourceUrl {
    const parsed = this.stripSpaces(this.numberParser(n));

    return parsed ? this.sanitizer.bypassSecurityTrustResourceUrl(parsed) : '';
  }

  private getDurationFromEntry(entry: CallLogEvent): string {
    if (entry.lastEvent === CALL_LOG_EVENT_TYPE.CALL_LOST_BUSY_LINES) {
      return 'busy-lines';
    }

    const callEnding = this.isAnswered(entry)
      ? CALL_ENDING_TYPE[entry.direction]['ANSWERED']
      : CALL_ENDING_TYPE[entry.direction]['UNANSWERED'];

    switch (callEnding) {
      case CALL_ENDING_TYPE[CALL_LOG_EVENT_DIRECTION.INBOUND]['UNANSWERED']:
        return 'lost';
      case CALL_ENDING_TYPE[CALL_LOG_EVENT_DIRECTION.OUTBOUND]['UNANSWERED']:
      case CALL_ENDING_TYPE[CALL_LOG_EVENT_DIRECTION.INTERNAL]['UNANSWERED']:
        return 'unanswered';
    }
  }

  private hasAiData(call: CallLogEvent): boolean {
    return (
      ![null, undefined].includes(call.summary) &&
      ![null, undefined].includes(call.sentiment_score) &&
      ![null, undefined].includes(call.transcription)
    );
  }
}
